/**
 * Intrinsic Apps popover layout. Width follows app count (column count), not
 * window size. Columns drop only when the preferred grid cannot fit.
 */

/** Fixed cell size so icon + label never collide. Matches `w-20`. */
export const APPS_POPOVER_CELL_PX = 80;
/** Matches Tailwind `gap-3`. */
export const APPS_POPOVER_GAP_PX = 12;
/** Matches Tailwind `p-4` on the popover shell. */
export const APPS_POPOVER_PAD_PX = 16;
/** `border` on the popover shell (1px each side). */
export const APPS_POPOVER_BORDER_PX = 2;
export const APPS_POPOVER_MAX_COLS = 4;
/** Minimum space to keep the popover off the viewport edges. */
export const APPS_POPOVER_VIEWPORT_GUTTER_PX = 16;

export interface AppsPopoverLayout {
  /** Columns from app count, before viewport fitting. */
  preferredColumnCount: number;
  /** Columns after shrinking for a too-narrow viewport. */
  columnCount: number;
  /** Pixel width of the popover (padding + cells + gaps). */
  widthPx: number;
}

/**
 * Column count from how many apps are in the grid (capped at 4).
 *
 * @param appCount - Number of launcher entries
 */
export function getAppsPopoverColumnCount(appCount: number): number {
  if (appCount <= 0) return 1;
  if (appCount <= APPS_POPOVER_MAX_COLS) return appCount;
  return Math.min(APPS_POPOVER_MAX_COLS, Math.ceil(Math.sqrt(appCount)));
}

/**
 * Content width for a given column count.
 *
 * @param columnCount - Number of grid columns
 */
export function getAppsPopoverWidthPx(columnCount: number): number {
  const cols = Math.max(1, columnCount);
  return (
    APPS_POPOVER_BORDER_PX +
    APPS_POPOVER_PAD_PX * 2 +
    cols * APPS_POPOVER_CELL_PX +
    (cols - 1) * APPS_POPOVER_GAP_PX
  );
}

/**
 * How many preferred columns actually fit in the viewport.
 *
 * @param preferredColumnCount - Columns from app count
 * @param viewportWidthPx - `window.innerWidth`
 */
export function getAppsPopoverFittedColumnCount(
  preferredColumnCount: number,
  viewportWidthPx: number,
): number {
  const available =
    viewportWidthPx -
    APPS_POPOVER_VIEWPORT_GUTTER_PX * 2 -
    APPS_POPOVER_PAD_PX * 2;
  const maxFit = Math.max(
    1,
    Math.floor(
      (available + APPS_POPOVER_GAP_PX) /
        (APPS_POPOVER_CELL_PX + APPS_POPOVER_GAP_PX),
    ),
  );
  return Math.min(Math.max(1, preferredColumnCount), maxFit);
}

/**
 * Apps popover size: app-count columns, reduced only on very small viewports.
 *
 * @param appCount - Number of launcher entries
 * @param viewportWidthPx - `window.innerWidth`
 */
export function getAppsPopoverLayout(
  appCount: number,
  viewportWidthPx: number,
): AppsPopoverLayout {
  const preferredColumnCount = Math.min(
    getAppsPopoverColumnCount(appCount),
    appCount,
  );
  const columnCount = getAppsPopoverFittedColumnCount(
    preferredColumnCount,
    viewportWidthPx,
  );
  return {
    preferredColumnCount,
    columnCount,
    widthPx: getAppsPopoverWidthPx(columnCount),
  };
}

/**
 * Popover width for a single row of `appCount` icons (used for positioning).
 */
export function getAppsPopoverRowWidthPx(appCount: number): number {
  if (appCount <= 0) return getAppsPopoverWidthPx(1);
  const cols = Math.min(appCount, APPS_POPOVER_MAX_COLS);
  return getAppsPopoverWidthPx(cols);
}

/**
 * Horizontal center for the popover.
 *
 * Prefers the apps-button center, then clamps to the viewport gutters.
 * If the popover is wider than the viewport minus gutters, falls back to
 * the viewport midpoint (edges may still clip on extremely narrow screens).
 *
 * @param preferredCenterX - Apps button center in viewport coordinates
 * @param widthPx - Popover width
 * @param viewportWidthPx - `window.innerWidth`
 */
export function getAppsPopoverCenterX(
  preferredCenterX: number,
  widthPx: number,
  viewportWidthPx: number,
): number {
  const half = widthPx / 2;
  const minCenter = APPS_POPOVER_VIEWPORT_GUTTER_PX + half;
  const maxCenter = viewportWidthPx - APPS_POPOVER_VIEWPORT_GUTTER_PX - half;
  if (minCenter >= maxCenter) {
    return viewportWidthPx / 2;
  }
  return Math.min(Math.max(preferredCenterX, minCenter), maxCenter);
}
